/**
 * RummyGame — Main container orchestrating game flow, AI turns, state.
 * Wrapped in ErrorBoundary componentName="Rummy".
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { RummyEngine } from '../RummyEngine';
import { RummyAI } from '../RummyAI';
import { RummyBoard } from './RummyBoard';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { RoomCodeDisplay } from '../../components/RoomCodeDisplay';
import { MultiplayerRummyService } from '../services/MultiplayerRummyService';
import { WebSocketService } from '../../services/WebSocketService';
import { NotificationService } from '../../services/NotificationService';
import { GameResult } from '../../services/multiplayer/GenericMultiplayerService';
import { AISpeedSlider } from '../../components/AISpeedSlider';
import {
  RummyGameProps, RummyGameSettings, RummyAction,
  VisibleRummyState, RummyGameConfig,
} from '../types';
import './RummyGame.css';

type GameView = 'mode-select' | 'playing' | 'game-over';

const RummyGameInner: React.FC<RummyGameProps> = ({ onExit, initialRoomCode }) => {
  const { isAuthenticated } = useAuthentication();
  const { addNotification } = useNotifications();

  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<RummyGameSettings | null>(null);
  const [visibleState, setVisibleState] = useState<VisibleRummyState | null>(null);
  const [selectedCards, setSelectedCards] = useState<Set<string>>(new Set());

  // AI speed
  const [aiSpeedDelay, setAiSpeedDelay] = useState(500);
  const aiSpeedDelayRef = useRef(500);

  // Multiplayer state
  const [multiplayerService] = useState<MultiplayerRummyService>(
    () => new MultiplayerRummyService(WebSocketService.getInstance())
  );
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [opponentConnected, setOpponentConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('connected');

  const engineRef = useRef(new RummyEngine());
  const aiRef = useRef<Map<string, RummyAI>>(new Map());
  const sessionIdRef = useRef<string | null>(null);
  const gsm = useRef(GameSessionManager.getInstance());
  const humanPlayerId = useRef('player-human');
  const hasAttemptedJoin = useRef(false);
  const isCreatingRoom = useRef(false);
  const aiTurnInProgress = useRef(false);

  const refreshState = useCallback(() => {
    const state = engineRef.current.getVisibleState(humanPlayerId.current);
    setVisibleState(state);
    return state;
  }, []);

  const handleAiSpeedChange = useCallback((delay: number) => {
    setAiSpeedDelay(delay);
    aiSpeedDelayRef.current = delay;
  }, []);

  // =========================================================================
  // Multiplayer event handlers
  // =========================================================================
  useEffect(() => {
    multiplayerService.onGameStarted(() => {
      setWaitingForOpponent(false);
      setOpponentConnected(true);
    });

    multiplayerService.onMoveUpdate((move: RummyAction, gameState: any) => {
      if (gameState) setVisibleState(gameState);
    });

    multiplayerService.onGameOver((result: GameResult) => {
      const message = result.result === 'win'
        ? `🎉 ${result.winner} wins!`
        : 'Game over!';
      NotificationService.getInstance().sendInfoNotification(message);
    });

    multiplayerService.onPlayerDisconnected(() => {
      setOpponentConnected(false);
      NotificationService.getInstance().sendWarningNotification(
        'Opponent disconnected. Waiting for reconnection...'
      );
    });

    multiplayerService.onPlayerConnected(() => {
      setOpponentConnected(true);
      NotificationService.getInstance().sendSuccessNotification('Opponent reconnected!');
    });

    multiplayerService.onGameStateReceived((gameState: any) => {
      if (gameState) setVisibleState(gameState);
    });

    multiplayerService.onConnectionStatus((status) => {
      setConnectionStatus(status);
      if (status === 'reconnecting') {
        NotificationService.getInstance().sendWarningNotification('Connection lost. Reconnecting...');
      } else if (status === 'connected') {
        NotificationService.getInstance().sendSuccessNotification('Connected!');
      } else if (status === 'disconnected') {
        NotificationService.getInstance().sendErrorNotification('Disconnected from server');
      }
    });

    multiplayerService.onError((error: string) => {
      NotificationService.getInstance().sendErrorNotification(`Multiplayer error: ${error}`);
    });
  }, [multiplayerService]);

  // =========================================================================
  // Deep link room code handling
  // =========================================================================
  useEffect(() => {
    if (initialRoomCode && !roomCode && !hasAttemptedJoin.current) {
      hasAttemptedJoin.current = true;
      setView('playing');
      setRoomCode(initialRoomCode);
      setWaitingForOpponent(true);

      multiplayerService.joinRoom(initialRoomCode)
        .then(() => {
          setWaitingForOpponent(false);
          setOpponentConnected(true);
        })
        .catch((error: any) => {
          NotificationService.getInstance().sendErrorNotification(
            `Unable to join room ${initialRoomCode}. ${error.message || 'Please check the room code.'}`
          );
          setView('mode-select');
          setWaitingForOpponent(false);
          setRoomCode(null);
        });
    }
  }, [initialRoomCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // =========================================================================
  // Game Start
  // =========================================================================
  const handleStartGame = useCallback((newSettings: RummyGameSettings) => {
    // Handle multiplayer room creation
    if (newSettings.mode === 'multiplayer-realtime') {
      if (isCreatingRoom.current) return;
      isCreatingRoom.current = true;

      multiplayerService.createRoom()
        .then((room) => {
          setRoomCode(room.code);
          setWaitingForOpponent(true);
          setSettings(newSettings);
          setView('playing');
          isCreatingRoom.current = false;
        })
        .catch((error: any) => {
          NotificationService.getInstance().sendErrorNotification(
            `Failed to create room: ${error.message || 'Unknown error'}`
          );
          isCreatingRoom.current = false;
        });
      return;
    }

    const engine = engineRef.current;
    const aiCount = newSettings.aiOpponentCount || 3;
    const totalPlayers = newSettings.mode === 'local-multiplayer'
      ? (newSettings.playerCount || 2)
      : aiCount + 1;

    const players = newSettings.mode === 'local-multiplayer'
      ? Array.from({ length: totalPlayers }, (_, i) => ({
          id: i === 0 ? humanPlayerId.current : `player-${i + 1}`,
          name: i === 0 ? 'Player 1' : `Player ${i + 1}`,
          isHuman: true,
        }))
      : [
          { id: humanPlayerId.current, name: 'You', isHuman: true },
          ...Array.from({ length: aiCount }, (_, i) => ({
            id: `ai-${i + 1}`,
            name: `Bot ${i + 1}`,
            isHuman: false,
            aiDifficulty: newSettings.aiDifficulty || 'medium' as const,
          })),
        ];

    const config: RummyGameConfig = {
      playerCount: totalPlayers,
      players,
    };

    engine.initializeGame(config);

    // Create AI instances
    aiRef.current.clear();
    if (newSettings.mode === 'singleplayer') {
      config.players.filter(p => !p.isHuman).forEach(p => {
        aiRef.current.set(p.id, new RummyAI(p.aiDifficulty || 'medium'));
      });
    }

    setSettings(newSettings);
    setView('playing');
    setSelectedCards(new Set());
    sessionIdRef.current = gsm.current.startGameSession('rummy');

    // Start first round
    engine.startNewRound();
    refreshState();

    addNotification({
      type: NotificationType.SUCCESS,
      title: 'Game Started',
      message: `Indian Rummy${newSettings.aiDifficulty ? ` - ${newSettings.aiDifficulty} difficulty` : ''}`,
      duration: 2000,
    });

    // Check if AI goes first
    scheduleAITurnIfNeeded();
  }, [addNotification, refreshState, multiplayerService]);

  // =========================================================================
  // AI Turn Logic
  // =========================================================================
  const scheduleAITurnIfNeeded = useCallback(() => {
    const engine = engineRef.current;
    const currentId = engine.getCurrentPlayer();
    if (!currentId || currentId === humanPlayerId.current) return;
    if (engine.isGameOver() || !engine.isRoundInProgress()) return;
    if (aiTurnInProgress.current) return;

    const ai = aiRef.current.get(currentId);
    if (!ai) return;

    aiTurnInProgress.current = true;

    const hand = engine.getPlayerHand(currentId);
    const state = engine.getVisibleState(currentId);

    setTimeout(() => {
      ai.getAction(state, currentId, hand).then(action => {
        aiTurnInProgress.current = false;
        if (engine.isGameOver() || !engine.isRoundInProgress()) return;

        const result = engine.submitAction(currentId, action);

        if (result.success) {
          const newState = refreshState();

          if (result.roundComplete) {
            handleRoundComplete(newState, result.declarationValid, currentId);
            return;
          }

          // Continue AI turns
          scheduleAITurnIfNeeded();
        } else {
          // AI made invalid move — try drawing from stock and discarding
          aiTurnInProgress.current = false;
          const fallbackDraw = engine.submitAction(currentId, { type: 'draw-stock' });
          if (fallbackDraw.success) {
            const updatedHand = engine.getPlayerHand(currentId);
            if (updatedHand.length > 0) {
              engine.submitAction(currentId, { type: 'discard', cardId: updatedHand[updatedHand.length - 1].id });
            }
            refreshState();
            scheduleAITurnIfNeeded();
          }
        }
      }).catch(() => {
        aiTurnInProgress.current = false;
      });
    }, aiSpeedDelayRef.current);
  }, [refreshState]);

  // =========================================================================
  // Round / Game Completion
  // =========================================================================
  const handleRoundComplete = useCallback((state: VisibleRummyState, declarationValid?: boolean, winnerId?: string) => {
    const winnerName = state.players.find(p => p.id === winnerId)?.name || 'Unknown';

    addNotification({
      type: NotificationType.SUCCESS,
      title: `Round ${state.roundNumber} Complete`,
      message: declarationValid
        ? `${winnerName} declared and won!`
        : `${winnerName} wins the round!`,
      duration: 4000,
    });

    setView('game-over');

    if (sessionIdRef.current) {
      gsm.current.endGameSession(sessionIdRef.current, true);
      sessionIdRef.current = null;
    }
  }, [addNotification]);

  // =========================================================================
  // Player Actions
  // =========================================================================
  const handleCardSelect = useCallback((cardId: string) => {
    setSelectedCards(prev => {
      const next = new Set(prev);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else {
        // For discard, only allow one card selected
        next.clear();
        next.add(cardId);
      }
      return next;
    });
  }, []);

  const handleDrawStock = useCallback(() => {
    const result = engineRef.current.submitAction(humanPlayerId.current, { type: 'draw-stock' });
    if (result.success) {
      setSelectedCards(new Set());
      refreshState();
    } else {
      addNotification({
        type: NotificationType.WARNING,
        title: 'Cannot Draw',
        message: result.error || 'Invalid action',
        duration: 2000,
      });
    }
  }, [refreshState, addNotification]);

  const handleDrawDiscard = useCallback(() => {
    const result = engineRef.current.submitAction(humanPlayerId.current, { type: 'draw-discard' });
    if (result.success) {
      setSelectedCards(new Set());
      refreshState();
    } else {
      addNotification({
        type: NotificationType.WARNING,
        title: 'Cannot Draw',
        message: result.error || 'Invalid action',
        duration: 2000,
      });
    }
  }, [refreshState, addNotification]);

  const handleDiscard = useCallback((cardId: string) => {
    const result = engineRef.current.submitAction(humanPlayerId.current, { type: 'discard', cardId });
    if (result.success) {
      setSelectedCards(new Set());
      refreshState();
      // Schedule AI turn
      scheduleAITurnIfNeeded();
    } else {
      addNotification({
        type: NotificationType.WARNING,
        title: 'Cannot Discard',
        message: result.error || 'Invalid action',
        duration: 2000,
      });
    }
  }, [refreshState, addNotification, scheduleAITurnIfNeeded]);

  const handleDeclare = useCallback(() => {
    const result = engineRef.current.submitAction(humanPlayerId.current, { type: 'declare' });
    if (result.success) {
      const state = refreshState();
      handleRoundComplete(state, result.declarationValid, humanPlayerId.current);
    } else {
      addNotification({
        type: NotificationType.ERROR,
        title: 'Invalid Declaration',
        message: result.error || 'Your hand does not form valid melds',
        duration: 4000,
      });
    }
  }, [refreshState, addNotification, handleRoundComplete]);

  const handleSort = useCallback(() => {
    engineRef.current.submitAction(humanPlayerId.current, { type: 'sort' });
    refreshState();
  }, [refreshState]);

  const handleDrop = useCallback(() => {
    const result = engineRef.current.submitAction(humanPlayerId.current, { type: 'drop' });
    if (result.success) {
      const state = refreshState();
      if (result.roundComplete) {
        handleRoundComplete(state, false, state.gameStatus.type === 'roundComplete' ? (state.gameStatus as any).winnerId : undefined);
      } else {
        addNotification({
          type: NotificationType.INFO,
          title: 'Dropped',
          message: 'You dropped from this round.',
          duration: 2000,
        });
      }
    }
  }, [refreshState, addNotification, handleRoundComplete]);

  // =========================================================================
  // Navigation
  // =========================================================================
  const handleJoinPrivateGame = useCallback(async (code: string) => {
    setRoomCode(code);
    setWaitingForOpponent(true);
    setView('playing');

    try {
      await multiplayerService.joinRoom(code);
      setWaitingForOpponent(false);
      setOpponentConnected(true);
    } catch (error: any) {
      NotificationService.getInstance().sendErrorNotification(
        `Unable to join room ${code}. ${error.message || 'Please check the room code.'}`
      );
      setView('mode-select');
      setWaitingForOpponent(false);
      setRoomCode(null);
    }
  }, [multiplayerService]);

  const handleCancelWaiting = useCallback(() => {
    multiplayerService.leaveGame();
    setWaitingForOpponent(false);
    setRoomCode(null);
    setView('mode-select');
    setSettings(null);
    isCreatingRoom.current = false;
  }, [multiplayerService]);

  const handleNewGame = useCallback(() => {
    if (roomCode) {
      multiplayerService.leaveGame();
    }
    aiRef.current.clear();
    aiTurnInProgress.current = false;
    setView('mode-select');
    setVisibleState(null);
    setSelectedCards(new Set());
    setSettings(null);
    setRoomCode(null);
    setWaitingForOpponent(false);
    setOpponentConnected(false);
    setConnectionStatus('connected');
    isCreatingRoom.current = false;
  }, [roomCode, multiplayerService]);

  const handleExit = useCallback(() => {
    if (sessionIdRef.current) {
      gsm.current.endGameSession(sessionIdRef.current, false);
      sessionIdRef.current = null;
    }
    onExit?.();
  }, [onExit]);

  // Trigger AI on initial load if AI goes first
  useEffect(() => {
    if (view === 'playing' && visibleState) {
      scheduleAITurnIfNeeded();
    }
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  // =========================================================================
  // Render
  // =========================================================================

  if (view === 'mode-select') {
    const rummyConfig: GameModeConfig = {
      gameName: 'Indian Rummy',
      gameIcon: '🃏',
      subtitle: 'Classic card game',
      availableModes: ['ai', 'online'],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.mode === 'ai') {
        handleStartGame({
          mode: 'singleplayer',
          aiDifficulty: (selection.difficulty as any) || 'medium',
          aiOpponentCount: 3,
        });
      } else if (selection.mode === 'online') {
        handleStartGame({ mode: 'multiplayer-realtime' });
      }
    };

    return (
      <div className="rummy-game">
        <div className="rummy-game__header">
          <button className="rummy-game__exit-btn" onClick={handleExit} aria-label="Back to Game Hub">
            ← Game Hub
          </button>
        </div>
        <UniversalGameModeSelector
          config={rummyConfig}
          onStart={handleModeSelection}
          onJoinRoom={handleJoinPrivateGame}
          onCreateRoom={() => handleStartGame({ mode: 'multiplayer-realtime' })}
          isAuthenticated={isAuthenticated}
          guestGamesRemaining={isAuthenticated ? undefined : 3}
        />
      </div>
    );
  }

  if (!visibleState) {
    if (waitingForOpponent && roomCode) {
      return (
        <div className="rummy-game">
          <RoomCodeDisplay
            roomCode={roomCode}
            gameType="rummy"
            playerName="Guest"
            maxPlayers={6}
            onCancel={handleCancelWaiting}
            showCancelButton={true}
          />
        </div>
      );
    }
    return null;
  }

  const humanPlayer = visibleState.players.find(p => p.id === humanPlayerId.current);
  const isMyTurn = visibleState.players[visibleState.currentPlayerIndex]?.id === humanPlayerId.current;

  const gameOverConfig = view === 'game-over' ? {
    show: true,
    result: ((visibleState.gameStatus as any).winnerId === humanPlayerId.current ? 'win' : 'loss') as 'win' | 'loss',
    title: '🎉 Round Over!',
    subtitle: (visibleState.gameStatus as any).winnerId === humanPlayerId.current
      ? '🏆 You Won!'
      : `${visibleState.players.find(p => p.id === (visibleState.gameStatus as any).winnerId)?.name || 'AI'} Wins!`,
    icon: (visibleState.gameStatus as any).winnerId === humanPlayerId.current ? '🏆' : '😔',
    onPlayAgain: handleNewGame,
    onExit: handleExit,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Indian Rummy',
    board: (
      <div className="rummy-game__content">
        <RummyBoard
          gameState={visibleState}
          currentPlayerId={humanPlayerId.current}
          selectedCards={selectedCards}
          onCardSelect={handleCardSelect}
          onDrawStock={handleDrawStock}
          onDrawDiscard={handleDrawDiscard}
          onDiscard={handleDiscard}
          onDeclare={handleDeclare}
          onSort={handleSort}
          onDrop={handleDrop}
          disabled={!isMyTurn || view === 'game-over'}
        />
      </div>
    ),
    leftPanels: [
      {
        id: 'scores',
        title: 'Scores',
        icon: '📊',
        content: (
          <div className="rummy-game__overlay-scores">
            {visibleState.players.map(p => (
              <div key={p.id} className="rummy-game__score-row">
                <span className="rummy-game__score-name">{p.name}</span>
                <span className="rummy-game__score-value">{p.score} pts</span>
                <span className="rummy-game__score-status">
                  {p.status === 'declared' ? '✅' : p.status === 'dropped' ? '🚪' : ''}
                </span>
              </div>
            ))}
          </div>
        ),
      },
    ],
    statusBar: {
      turnIndicator: (
        <span>
          {view === 'game-over'
            ? 'Round Over'
            : isMyTurn
              ? '🎯 Your Turn'
              : `⏳ Waiting for ${visibleState.players[visibleState.currentPlayerIndex]?.name}...`}
        </span>
      ),
      gameStatus: view === 'game-over' ? 'Round Over' : `Round ${visibleState.roundNumber}`,
      gameStatusType: view === 'game-over' ? 'ended' : 'active',
      aiThinking: !isMyTurn && view !== 'game-over',
      connectionStatus: settings?.mode === 'multiplayer-realtime' ? connectionStatus : undefined,
      roomCode: roomCode || undefined,
    },
    actions: [
      { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
      { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
    ],
    secondaryActions: settings?.mode === 'singleplayer' ? [
      <AISpeedSlider key="ai-speed" value={aiSpeedDelay} onChange={handleAiSpeedChange} />,
    ] : undefined,
    gameOver: gameOverConfig,
    roomCode: roomCode || undefined,
  };

  return (
    <div className="rummy-game">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

const RummyGame: React.FC<RummyGameProps> = (props) => (
  <ErrorBoundary componentName="Rummy">
    <RummyGameInner {...props} />
  </ErrorBoundary>
);

export { RummyGame };
export default RummyGame;
